import React, { Fragment, useState } from 'react';

import { parseLocaleNumber } from 'Helpers/currencyFormat';
import { formatFigure, formatMoneyWithLocale, formatSeparateByThousands } from 'Helpers/formatService';

import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// A committed price comes straight out of stored amount_data, so a string is parsed the way formatMoneyWithLocale reads it.
const parsePrice = (value) => (typeof value === 'string' ? parseLocaleNumber(value) : value);

const BG_COLORS = ['#F6F6F6', '#F2F2F2', '#EDEDED', '#E9E9E9'];

export default function ProcurementExpenseTable({ expensesTree, total, maxQuantities, committed, viewOnly, onRemove, onEdit, lockedExpenseIds }) {
    if (!expensesTree?.expenses?.length && !expensesTree?.projects?.length && !expensesTree?.templates?.length && !expensesTree?.packs?.length) {
        return null;
    }

    return (
        <div>
            <h4 className="table__header tw-mb-6">{$t('Expenses')}</h4>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th>{$t('Expense Name')}</th>
                            <th>{$t('Department / Cost Centre')}</th>
                            <th>{$t('Type')}</th>
                            <th>{$t('Price')}</th>
                            {!!maxQuantities &&
                                Array.from({ length: maxQuantities }, (_, index) => (
                                    <th key={'qty-' + index}>
                                        {$t('Qty')} {maxQuantities > 1 ? index + 1 : ''}
                                    </th>
                                ))}
                            <th>{$t('Total')}</th>
                            {!!onRemove && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {expensesTree.expenses?.map((expense) => (
                            <ProcurementExpensesTableRow
                                key={expense.id}
                                table_row_key={expense.id}
                                expense={expense}
                                maxQuantities={maxQuantities}
                                committed={committed}
                                viewOnly={viewOnly}
                                onRemove={onRemove}
                                lockedExpenseIds={lockedExpenseIds}
                            />
                        ))}
                        {expensesTree.projects?.map((project) => (
                            <ProcurementExpensesTableProjectGroupRow
                                key={project.id}
                                project_key={project.id}
                                project={project}
                                maxQuantities={maxQuantities}
                                committed={committed}
                                viewOnly={viewOnly}
                                onRemove={onRemove}
                                lockedExpenseIds={lockedExpenseIds}
                            />
                        ))}
                        {expensesTree.packs?.map((pack) => (
                            <ProcurementExpensesTablePackGroupRow
                                key={pack.id}
                                group_key={pack.id}
                                pack={pack}
                                maxQuantities={maxQuantities}
                                committed={committed}
                                viewOnly={viewOnly}
                                onRemove={onRemove}
                                onEdit={onEdit}
                                lockedExpenseIds={lockedExpenseIds}
                            />
                        ))}
                        {expensesTree.templates?.map((template) => (
                            <ProcurementExpensesTableTemplateGroupRow
                                key={template.id}
                                template_key={template.id}
                                template={template}
                                maxQuantities={maxQuantities}
                                committed={committed}
                                viewOnly={viewOnly}
                                onRemove={onRemove}
                                onEdit={onEdit}
                                lockedExpenseIds={lockedExpenseIds}
                            />
                        ))}

                        <tr>
                            <td colSpan={(onRemove ? 4 : 3) + (maxQuantities ?? 0)}></td>
                            <td className="tw-font-semibold inbud-numeric-cell !tw-border-r-0">{formatFigure(total)}</td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ProcurementExpensesTableGroupRow({
    group_key,
    expense,
    subExpenses,
    groupId,
    level,
    maxQuantities,
    onRemove,
    onEdit,
    children,
    committed = false,
    viewOnly = false,
    lockedExpenseIds,
}) {
    const [show, setShow] = useState(expense?.show || false);

    if (!subExpenses?.length && !children) {
        return null;
    }

    return (
        <Fragment key={group_key}>
            <ProcurementExpensesTableRow
                group_key={group_key}
                table_row_key={expense.id + groupId}
                expense={expense}
                groupId={groupId}
                level={level}
                maxQuantities={maxQuantities}
                onRemove={onRemove}
                onEdit={onEdit}
                toggleShow={setShow}
                show={show}
                committed={committed}
                viewOnly={viewOnly}
                lockedExpenseIds={lockedExpenseIds}
            />
            {show && (
                <>
                    {subExpenses?.map((subExpense) => (
                        <ProcurementExpensesTableRow
                            group_key={group_key}
                            key={subExpense.id}
                            backgroundColor={BG_COLORS[level] || '#FBFBFB'}
                            table_row_key={subExpense.id}
                            expense={subExpense}
                            level={level + 1}
                            maxQuantities={maxQuantities}
                            onRemove={onRemove}
                            onEdit={onEdit}
                            committed={committed}
                            viewOnly={viewOnly}
                            lockedExpenseIds={lockedExpenseIds}
                        />
                    ))}
                    {children}
                </>
            )}
        </Fragment>
    );
}

function ProcurementExpensesTableProjectGroupRow({ project_key, project, committed, onRemove, maxQuantities, viewOnly, lockedExpenseIds }) {
    const expenses = project?.expenses;
    const packs = project?.packs;
    const templates = project?.templates;

    return (
        <Fragment key={project_key}>
            <ProcurementExpensesTableGroupRow
                key={project.id}
                expense={project}
                subExpenses={expenses}
                groupId={`expense-${project.id}`}
                level={0}
                maxQuantities={maxQuantities}
                committed={committed}
                viewOnly={viewOnly}
                onRemove={onRemove}
                lockedExpenseIds={lockedExpenseIds}
            >
                {packs?.map((pack) => (
                    <ProcurementExpensesTableGroupRow
                        key={pack.id}
                        expense={pack}
                        subExpenses={pack?.expenses}
                        groupId={`expense-${pack.id}`}
                        level={1}
                        maxQuantities={maxQuantities}
                        committed={committed}
                        viewOnly={viewOnly}
                        onRemove={onRemove}
                        lockedExpenseIds={lockedExpenseIds}
                    />
                ))}
                {templates?.map((template) => (
                    <ProcurementExpensesTableGroupRow
                        key={template.id}
                        expense={template}
                        subExpenses={template?.expenses}
                        groupId={`expense-${template.id}`}
                        level={1}
                        maxQuantities={maxQuantities}
                        committed={committed}
                        viewOnly={viewOnly}
                        onRemove={onRemove}
                        lockedExpenseIds={lockedExpenseIds}
                    >
                        {template?.dividers?.map((divider) => (
                            <ProcurementExpensesTableGroupRow
                                key={divider.id}
                                expense={divider}
                                subExpenses={divider?.expenses}
                                groupId={`expense-${divider.id}`}
                                level={2}
                                maxQuantities={maxQuantities}
                                committed={committed}
                                viewOnly={viewOnly}
                                onRemove={onRemove}
                                lockedExpenseIds={lockedExpenseIds}
                            />
                        ))}
                    </ProcurementExpensesTableGroupRow>
                ))}
            </ProcurementExpensesTableGroupRow>
        </Fragment>
    );
}

function ProcurementExpensesTablePackGroupRow({ group_key, pack, committed, viewOnly, onRemove, maxQuantities, onEdit, lockedExpenseIds }) {
    const expenses = pack?.expenses;
    return (
        <ProcurementExpensesTableGroupRow
            key={group_key}
            group_key={group_key}
            expense={pack}
            subExpenses={expenses}
            groupId={`expense-${pack.id}`}
            level={0}
            maxQuantities={maxQuantities}
            committed={committed}
            viewOnly={viewOnly}
            onRemove={onRemove}
            onEdit={onEdit}
            lockedExpenseIds={lockedExpenseIds}
        />
    );
}

function ProcurementExpensesTableTemplateGroupRow({ template_key, template, onRemove, onEdit, maxQuantities, committed, viewOnly, lockedExpenseIds }) {
    const expenses = template?.expenses;
    const dividers = template?.dividers;

    return (
        <Fragment key={template_key}>
            <ProcurementExpensesTableGroupRow
                key={template.id}
                expense={template}
                subExpenses={expenses}
                groupId={`expense-${template.id}`}
                level={0}
                maxQuantities={maxQuantities}
                committed={committed}
                viewOnly={viewOnly}
                onRemove={onRemove}
                onEdit={onEdit}
                lockedExpenseIds={lockedExpenseIds}
            >
                {dividers?.map((divider) => (
                    <ProcurementExpensesTableGroupRow
                        key={divider.id}
                        expense={divider}
                        subExpenses={divider?.expenses}
                        groupId={`expense-${divider.id}`}
                        level={1}
                        maxQuantities={maxQuantities}
                        committed={committed}
                        viewOnly={viewOnly}
                        onRemove={onRemove}
                        onEdit={onEdit}
                        lockedExpenseIds={lockedExpenseIds}
                    />
                ))}
            </ProcurementExpensesTableGroupRow>
        </Fragment>
    );
}

function ProcurementExpensesTableRow({
    table_row_key,
    expense,
    maxQuantities,
    committed,
    viewOnly,
    groupId,
    level = 0,
    onRemove,
    show,
    toggleShow,
    onEdit,
    group_key = null,
    lockedExpenseIds,
}) {
    const isLocked = lockedExpenseIds && lockedExpenseIds.includes(expense.id);
    const lockedTooltip = expense.department
        ? $t('Owned by {{department}} — delete centrally', { department: expense.department })
        : $t('Owned by another department — delete centrally');

    return (
        <tr key={table_row_key} style={{ backgroundColor: BG_COLORS[level] || '#FBFBFB' + ' !important' }} className={isLocked ? 'tw-opacity-50' : ''}>
            <td style={{ paddingLeft: 6 * (level + 1) }}>
                {groupId ? (
                    <div className="tw-flex tw-items-center tw-gap-x-1.5">
                        {isLocked ? (
                            <Tooltip tooltipContent={lockedTooltip} tooltipType="text" offset={[16, 16]}>
                                <span>{expense.name}</span>
                            </Tooltip>
                        ) : (
                            expense.name
                        )}
                        {!show && (
                            <div className={'tw-cursor-pointer'} onClick={() => toggleShow(true)}>
                                <ChevronDownIcon className={'tw-h-3 tw-w-3'} />
                            </div>
                        )}
                        {!!show && (
                            <div className={'tw-cursor-pointer'} onClick={() => toggleShow(false)}>
                                <ChevronUpIcon className={'tw-h-3 tw-w-3'} />
                            </div>
                        )}
                    </div>
                ) : isLocked ? (
                    <Tooltip tooltipContent={lockedTooltip} tooltipType="text" offset={[16, 16]}>
                        <span>{expense.name}</span>
                    </Tooltip>
                ) : (
                    <>{expense.name}</>
                )}
            </td>
            <td>
                {expense.department} {expense.costcenter !== null ? ` / ${expense.costcenter}` : ``}
            </td>
            <td>{expense.transaction_type_formatted}</td>
            {groupId ? (
                <Fragment>
                    <td />
                    {!!maxQuantities && Array.from({ length: maxQuantities }, (_, index) => <td key={expense.id + '-qty-' + index}></td>)}
                    <td className="inbud-numeric-cell">{formatFigure(expense.total)}</td>
                </Fragment>
            ) : (
                <Fragment>
                    <td className="inbud-numeric-cell">
                        {formatFigure(
                            parsePrice(committed && expense.committed_amount.price ? expense.committed_amount.price : expense.price),
                            formatMoneyWithLocale,
                            {
                                precision: 3,
                            },
                        )}
                    </td>
                    {!!maxQuantities &&
                        Array.from({ length: maxQuantities }, (_, index) => {
                            const expenseAmounts = committed ? null : expense.amount;
                            const committedKeys = committed ? Object.keys(expense.committed_amount || {}).filter((k) => k !== 'total' && k !== 'price') : [];
                            const amount = committed
                                ? committedKeys[index] !== undefined
                                    ? expense.committed_amount[committedKeys[index]]
                                    : undefined
                                : expenseAmounts?.[index]?.amount;

                            return (
                                <td key={expense.id + '-qty-' + index} className="inbud-numeric-cell">
                                    {!isNaN(amount) ? formatFigure(amount, formatSeparateByThousands, { precision: 3 }) : ''}
                                </td>
                            );
                        })}
                    <td className="inbud-numeric-cell">{formatFigure(committed ? expense.total_committed : expense.total)}</td>
                </Fragment>
            )}
            {(!!onRemove || !!onEdit) && (
                <td>
                    {((!groupId && expense.type !== 'pack_child') || expense.type == 'pack') && (
                        <div className="tw-flex tw-gap-x-3">
                            {!!onRemove && (
                                <TextButton
                                    variant="danger"
                                    onClick={() => !isLocked && onRemove(expense.id)}
                                    disabled={viewOnly || isLocked}
                                    title={isLocked ? lockedTooltip : undefined}
                                >
                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-center" />
                                </TextButton>
                            )}
                            {!!onEdit && (
                                <TextButton variant="primary" onClick={() => onEdit(expense, group_key)} disabled={viewOnly || isLocked}>
                                    <EditIcon className="tw-w-3 tw-h-3 tw-text-center" />
                                </TextButton>
                            )}
                        </div>
                    )}
                </td>
            )}
        </tr>
    );
}
